<template>
    <div v-if="showCard" class="min-w-50 flex flex-col gap-2">
        <h3 class="scaffold-body text-valence-grey-850">
            <i class="text-lg/none mr-1 bi bi-graph-up" aria-hidden="true"></i>
            {{ props.title }}
        </h3>
        <div v-if="tonalThemeEnabled" class="flex flex-col gap-5 rounded-lgr bg-surface-sunken p-5 md:flex-row md:items-center md:justify-between md:p-7.5">
            <div class="flex items-center gap-6">
                <GoalSettingMark class="h-15.25 w-10 shrink-0 text-accent" />
                <div class="flex min-w-0 flex-col gap-1 text-ink">
                    <h3 class="nadia-title-subsection">{{ cardTitle }}</h3>
                    <p class="nadia-chat">{{ cardSubtitle }}</p>
                </div>
            </div>

            <div class="flex w-full shrink-0 flex-col gap-2 md:w-auto">
                <BaseLink
                    v-for="moment in liveMoments"
                    :key="moment.id"
                    :href="momentUrl(moment)"
                    class="base-btn-neutral inline-flex w-full items-center justify-center md:w-auto"
                    @click="logCtaClick(moment)">
                    {{ moment.title }}
                </BaseLink>
            </div>
        </div>
        <div v-else class="rounded-xl overflow-hidden border bg-linear-to-r from-[#886ba2] via-[#645abe] via-30% to-[#632ead] text-white p-6">
            <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div class="flex-1">
                    <h3 class="title-body">{{ cardTitle }}</h3>
                    <p class="mt-3 body-small">{{ cardSubtitle }}</p>
                </div>

                <div class="flex w-full flex-col gap-3 md:w-auto md:min-w-65">
                    <a
                        v-for="moment in liveMoments"
                        :key="moment.id"
                        :href="momentUrl(moment)"
                        class="group flex items-center justify-between gap-3 rounded-2xl px-5 py-4 text-left transition-colors duration-200 bg-white/10 text-white hover:bg-white/15"
                        @click="logCtaClick(moment)">
                        <span class="details md:body-small text-white">{{ moment.title }}</span>
                        <i class="bi bi-arrow-right text-white/80 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import GoalSettingMark from "~vue/icons/GoalSettingMark.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import type { PerformanceMoment } from "~vue/types/performance_management";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { DateTime } from "luxon";
import { computed, onMounted } from "vue";

interface Props {
    enabled: boolean;
    title: string;
    homepageCardTitle?: string;
    homepageCardSubtitle?: string;
    moments?: PerformanceMoment[];
}

const props = withDefaults(defineProps<Props>(), {
    title: "Performance Management",
    homepageCardTitle: "",
    homepageCardSubtitle: "",
    moments: () => [],
});

// A moment is "live" while today is within its date window, regardless of whether the
// user has completed its CTAs — always-on moments (e.g. year-round goal setting) should
// keep their button visible so users can return to refine or add goals (ENG-1330).
const liveMoments = computed(() => {
    const today = DateTime.now().toISODate();
    return props.moments.filter((moment) => moment.start_date <= today && moment.expiration_date > today);
});

const showCard = computed(() => props.enabled && liveMoments.value.length > 0);

const cardTitle = computed(() => props.homepageCardTitle || "Your Performance Review Timeline");
const cardSubtitle = computed(() => props.homepageCardSubtitle || "Track and manage your performance reviews in one place.");

const momentUrl = (moment: PerformanceMoment) => `/performance_management?moment_id=${moment.id}`;

onMounted(() => {
    if (showCard.value) {
        logUserInteraction(AmplitudeEventNames.MOMENTS_CARD_SHOWN, { source: "homepage_tile" });
    }
});

const logCtaClick = (moment: PerformanceMoment) => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_CARD_CTA_CLICKED, {
        label: moment.title,
        url: momentUrl(moment),
        moment_id: moment.id,
    });
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "homepage_card" });
};
</script>
